import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UploadSimple, DownloadSimple, FileXls, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export function ImportMembersDialog({ onImported }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/import/members-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "member_import_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Could not download template"); }
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/members", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
      onImported?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setFile(null); setResult(null); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="import-members-button">
          <UploadSimple size={18} weight="bold" /> Import Members
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Bulk import members</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">
          Upload a spreadsheet (.xlsx or .csv). Members are matched by Staff ID — existing
          records are updated, new ones are created with SSNIT as the initial password.
        </p>

        <button onClick={downloadTemplate} data-testid="download-template-button"
          className="flex items-center gap-2 text-sm font-medium text-primary hover:underline w-fit">
          <DownloadSimple size={16} weight="bold" /> Download template
        </button>

        <label className="mt-2 flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border px-4 py-8 cursor-pointer hover:bg-accent/40 transition-colors">
          <FileXls size={28} className="text-muted-foreground" />
          <span className="text-sm font-medium">{file ? file.name : "Choose a file to upload"}</span>
          <span className="text-xs text-muted-foreground">Facility / Chapter columns match by name</span>
          <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
            data-testid="import-file-input"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} />
        </label>

        {result && (
          <div data-testid="import-result" className="rounded-md bg-muted p-3 text-sm space-y-1">
            <p className="flex items-center gap-2 font-medium text-emerald-600 dark:text-emerald-400">
              <CheckCircle size={16} weight="fill" /> {result.created} created · {result.updated} updated · {result.total_rows} rows
            </p>
            {result.errors?.length > 0 && (
              <ul className="text-destructive list-disc pl-5">
                {result.errors.slice(0, 6).map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
          </div>
        )}

        <Button onClick={upload} disabled={!file || busy} data-testid="import-submit-button" className="w-full gap-2">
          <UploadSimple size={18} weight="bold" /> {busy ? "Importing…" : "Import"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
